/**
 * Chatbot State Manager
 * Manages persistent state for the chatbot across page navigations
 * Uses sessionStorage to maintain state during the session
 */

interface ChatbotState {
  greetingShown: boolean;
  sessionId: string | null;
  lastPageType: string | null;
  lastContentId: string | null;
  isPrivateSession: boolean;
}

// Type for DFMessenger element with internal message storage
interface DFMessengerWithState extends HTMLElement {
  messages?: Array<{
    type: string;
    name?: string;
    text?: string;
    payload?: string;
  }>;
  shadowRoot: ShadowRoot | null;
}

class ChatbotStateManager {
  private static instance: ChatbotStateManager;
  private readonly STORAGE_KEY = 'zngly_chatbot_state';
  private state: ChatbotState;

  private constructor() {
    this.state = this.loadState();
  }

  public static getInstance(): ChatbotStateManager {
    if (!ChatbotStateManager.instance) {
      ChatbotStateManager.instance = new ChatbotStateManager();
    }
    return ChatbotStateManager.instance;
  }

  private loadState(): ChatbotState {
    try {
      const stored = sessionStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      // console.error('Error loading chatbot state:', error);
    }

    return {
      greetingShown: false,
      sessionId: null,
      lastPageType: null,
      lastContentId: null,
      isPrivateSession: false,
    };
  }

  private saveState(): void {
    try {
      sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.state));
    } catch (error) {
      // console.error('Error saving chatbot state:', error);
    }
  }

  public hasGreetingBeenShown(): boolean {
    return this.state.greetingShown;
  }

  public markGreetingAsShown(): void {
    this.state.greetingShown = true;
    this.saveState();
  }

  public resetGreeting(): void {
    this.state.greetingShown = false;
    this.saveState();
  }

  public getSessionId(): string | null {
    return this.state.sessionId;
  }

  public setSessionId(id: string): void {
    this.state.sessionId = id;
    this.saveState();
  }

  public updatePageContext(pageType: string, contentId: string | null, isPrivate: boolean): void {
    this.state.lastPageType = pageType;
    this.state.lastContentId = contentId;
    this.state.isPrivateSession = isPrivate;
    this.saveState();
  }

  public shouldStartNewSession(pageType: string, contentId: string | null, isPrivate: boolean): boolean {
    // Always start new session for private content
    if (isPrivate) {
      // If we're moving from public to private
      if (!this.state.isPrivateSession) {
        return true;
      }
      // If we're moving between different private content
      if (contentId !== this.state.lastContentId) {
        return true;
      }
    } else {
      // Moving from private to public - need new session to clear private context
      if (this.state.isPrivateSession) {
        // Reset greeting flag when leaving private content
        this.state.greetingShown = false;
        this.saveState();
        return true;
      }
    }

    return false;
  }

  public clearState(): void {
    this.state = {
      greetingShown: false,
      sessionId: null,
      lastPageType: null,
      lastContentId: null,
      isPrivateSession: false,
    };
    this.saveState();
  }

  /**
   * Check if there's already a greeting element in the chat
   * This provides an additional safety check
   */
  public hasGreetingInDOM(): boolean {
    try {
      const df = document.querySelector('df-messenger') as DFMessengerWithState | null;
      if (!df) return false;

      // Try multiple methods to check for greeting

      // Method 1: Check in shadow DOM if accessible
      if (df.shadowRoot) {
        const greetings = df.shadowRoot.querySelectorAll('df-custom-greeting');
        if (greetings.length > 0) return true;

        // Also check for greeting in message containers
        const messages = df.shadowRoot.querySelectorAll('.message, .df-message, [class*="message"]');
        for (const msg of messages) {
          if (msg.querySelector('df-custom-greeting')) return true;
          // Check if the message contains greeting-like content
          const text = msg.textContent || '';
          if (text.includes('Welcome to') && text.includes('!')) return true;
        }
      }

      // Method 2: Check using the element's properly typed messages property
      if (df.messages && Array.isArray(df.messages)) {
        return df.messages.some((msg) => msg.type === 'custom_template' && msg.name === 'df-custom-greeting');
      }

      return false;
    } catch (error) {
      // console.error('Error checking DOM for greeting:', error);
      return false;
    }
  }
}

export const chatbotStateManager = ChatbotStateManager.getInstance();
